import pdfMake from "pdfmake/build/pdfmake"
import { DepartmentPerformanceAgreementDocument } from "./DepartmentPerformanceAgreementDocument"

import type { IObjective } from "../../../shared/models/Objective"
import type { IMeasureDepartment } from "../../../shared/models/MeasureDepartment"
import type { IMeasureCompany } from "../../../shared/models/MeasureCompany"
import type UserPerformanceData from "../../../shared/models/Report"

// Fixed department PDF generation
export const generateDepartmentPerformanceAgreementPDFNew = async (
  title: string,
  vision: string,
  mission: string,
  strategicObjectives: IObjective[],
  contributoryObjectives: IObjective[],
  measures: IMeasureDepartment[],
) => {
  try {
    const docDefinition = await DepartmentPerformanceAgreementDocument(
      title,
      vision,
      mission,
      strategicObjectives,
      contributoryObjectives,
      measures,
    )

    const newWindow = window.open()
    if (newWindow) {
      pdfMake.createPdf(docDefinition as any).open({}, newWindow)
    } else {
      // Fallback if popup is blocked
      pdfMake.createPdf(docDefinition as any).download(`${title}-Department-Performance-Agreement.pdf`)
    }
  } catch (error) {
    console.error("Error generating department PDF:", error)
    throw error
  }
}

// Company PDF generation (keeping existing working version)
// export const generateCompanyPerformanceAgreementPDF = async (
//   title: string,
//   vision: string,
//   mission: string,
//   objectives: IObjective[],
//   measures: IMeasureCompany[],
// ) => {
//   try {
//     const docDefinition = await CompanyPerformanceAgreementDocument(title, vision, mission, objectives, measures)

//     const newWindow = window.open()
//     if (newWindow) {
//       pdfMake.createPdf(docDefinition as any).open({}, newWindow)
//     } else {
//       pdfMake.createPdf(docDefinition as any).download(`${title}-Company-Performance-Agreement.pdf`)
//     }
//   } catch (error) {
//     console.error("Error generating company PDF:", error)
//     throw error
//   }
// }

// Enhanced report PDF generation
export const generateReportPDF = async (
  topQ2Performers: UserPerformanceData[],
  worstQ2Performers: UserPerformanceData[],
  topQ4Performers: UserPerformanceData[],
  worstQ4Performers: UserPerformanceData[],
) => {
  try {
    const docDefinition = await createReportDocument(
      topQ2Performers,
      worstQ2Performers,
      topQ4Performers,
      worstQ4Performers,
    )

    const newWindow = window.open()
    if (newWindow) {
      pdfMake.createPdf(docDefinition as any).open({}, newWindow)
    } else {
      pdfMake
        .createPdf(docDefinition as any)
        .download(`Performance-Report-${new Date().toISOString().split("T")[0]}.pdf`)
    }
  } catch (error) {
    console.error("Error generating report PDF:", error)
    throw error
  }
}

// Create report document structure
const createReportDocument = async (
  topQ2Performers: UserPerformanceData[],
  worstQ2Performers: UserPerformanceData[],
  topQ4Performers: UserPerformanceData[],
  worstQ4Performers: UserPerformanceData[],
) => {
  const currentDate = new Date().toLocaleDateString()

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    content: [
      {
        text: "Performance Management Report",
        style: "header",
        alignment: "center",
        margin: [0, 0, 0, 20],
      },
      {
        text: `Generated on: ${currentDate}`,
        style: "subheader",
        alignment: "center",
        margin: [0, 0, 0, 30],
      },

      // Top Q2 Performers
      {
        text: "Top Midterm Performers",
        style: "sectionHeader",
        margin: [0, 20, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "*", "auto"],
          body: [
            [
              { text: "#", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Department", style: "tableHeader" },
              { text: "Rating", style: "tableHeader" },
            ],
            ...topQ2Performers
              .slice(0, 10)
              .map((performer, index) => [
                (index + 1).toString(),
                performer.asJson.userName,
                performer.asJson.departmentName,
                performer.asJson.rating.toFixed(2),
              ]),
          ],
        },
        margin: [0, 0, 0, 20],
      },

      // Worst Q2 Performers
      {
        text: "Midterm Training & Coaching Required",
        style: "sectionHeader",
        margin: [0, 20, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "*", "auto"],
          body: [
            [
              { text: "#", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Department", style: "tableHeader" },
              { text: "Rating", style: "tableHeader" },
            ],
            ...worstQ2Performers
              .slice(0, 10)
              .map((performer, index) => [
                (index + 1).toString(),
                performer.asJson.userName,
                performer.asJson.departmentName,
                performer.asJson.rating.toFixed(2),
              ]),
          ],
        },
        margin: [0, 0, 0, 20],
      },

      // Top Q4 Performers
      {
        text: "Top Assessment Performers",
        style: "sectionHeader",
        margin: [0, 20, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "*", "auto"],
          body: [
            [
              { text: "#", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Department", style: "tableHeader" },
              { text: "Rating", style: "tableHeader" },
            ],
            ...topQ4Performers
              .slice(0, 10)
              .map((performer, index) => [
                (index + 1).toString(),
                performer.asJson.userName,
                performer.asJson.departmentName,
                performer.asJson.rating2.toFixed(2),
              ]),
          ],
        },
        margin: [0, 0, 0, 20],
      },

      // Worst Q4 Performers
      {
        text: "Assessment Training & Coaching Required",
        style: "sectionHeader",
        margin: [0, 20, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "*", "auto"],
          body: [
            [
              { text: "#", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Department", style: "tableHeader" },
              { text: "Rating", style: "tableHeader" },
            ],
            ...worstQ4Performers
              .slice(0, 10)
              .map((performer, index) => [
                (index + 1).toString(),
                performer.asJson.userName,
                performer.asJson.departmentName,
                performer.asJson.rating2.toFixed(2),
              ]),
          ],
        },
        margin: [0, 0, 0, 20],
      },
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        color: "#2c3e50",
      },
      subheader: {
        fontSize: 14,
        color: "#7f8c8d",
      },
      sectionHeader: {
        fontSize: 14,
        bold: true,
        color: "#34495e",
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: "white",
        fillColor: "#3498db",
      },
    },
  }
}
